import { SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { TweenConstants } from "@lib/titan2-slingo/dist/game/view/abstract/constant/TweenConstants";
import { ScatterWin } from "@lib/titan2-slingo/dist/game/view/abstract/display/ScatterWin";
import { TitanSpine } from "@lib/titan2/dist/game/control/TitanSpine";
import { TitanText } from "@lib/titan2/dist/game/control/TitanText";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { ScatterPickEvent } from "../../../controller/event/GameEvent";
import { TitanCurrencyTextSelector } from "../../../utils/FancyText/TitanCurrencyTextSelector";
import { TitanFancyTextFactory } from "../../../utils/FancyText/TitanFancyTextFactory";
import { GameSpineUtil } from "../../../utils/GameSpineUtil";
import { GameTextUtil } from "../../../utils/GameTextUtil";
/**
 * Shows a scatter win animation with win -> big win -> epic win -> mega win levels.
 */
export class GameScatterWin extends ScatterWin {
    create() {
        super.create();
        this.fireworkBurst = this.factory.createFireworkBurst();
        this.winTitle = TitanFancyTextFactory.CreateElement(this, this.langManager, this.landscapeProperties.winProperties, this.portraitProperties?.winProperties);
        this.bigWinTitle = TitanFancyTextFactory.CreateElement(this, this.langManager, this.landscapeProperties.bigWinProperties, this.portraitProperties?.bigWinProperties);
        this.epicWinTitle = TitanFancyTextFactory.CreateElement(this, this.langManager, this.landscapeProperties.epicWinProperties, this.portraitProperties?.epicWinProperties);
        this.megaWinTitle = TitanFancyTextFactory.CreateElement(this, this.langManager, this.landscapeProperties.megaWinProperties, this.portraitProperties?.megaWinProperties);
        // Unlike GameBigWin, we can also have a low win.
        this.lowWinTitle = TitanText.createField(this, this.landscapeProperties.lowWinTitleProperties, this.portraitProperties?.lowWinTitleProperties);
        this.lowWinCounter = TitanText.createField(this, this.landscapeProperties.lowWinCounterProperties, this.portraitProperties?.lowWinCounterProperties);
        this.lowWinAnimation = TitanSpine.createSpine(this, this.landscapeProperties.lowWinAnimationProperties, this.portraitProperties?.lowWinAnimationProperties);
        [this.winTitle, this.bigWinTitle, this.epicWinTitle, this.megaWinTitle].forEach(x => {
            if (x instanceof TitanText) {
                GameTextUtil.forceInitialise(x);
            }
        });
        GameTextUtil.forceInitialise(this.counter, this.lowWinTitle);
        DestroyUtil.destroy(this.counter);
        this.counter = TitanCurrencyTextSelector.createField(this.factory, this, this.landscapeProperties.bitmapCounter, this.portraitProperties?.bitmapCounter);
        GameSpineUtil.addToSlot(this.animation, this.counter, "dynamic-value");
    }
    createMouseBlocker() {
        super.createMouseBlocker();
        this.mouseBlocker.onClick(() => this.timelineAnimation?.seek(this.timelineAnimation.duration(), false), this);
    }
    reset() {
        super.reset();
        SpineUtil.reset(this.lowWinAnimation);
        this.timelineAnimation?.kill();
        this.timelineAnimation = undefined;
        this.mouseBlocker.interactiveChildren = false;
        this.visible = false;
    }
    addEventListeners() {
        super.addEventListeners();
        this.unregisterGame(SlingoEvent.SCATTER_WIN_ANIMATION);
        this.registerGame(ScatterPickEvent.PICK_RESULT, this.handlePlayScatterWin);
    }
    handleRotate(event) {
        super.handleRotate(event);
        GameSpineUtil.addToSlot(this.animation, this.fireworkBurst, "dynamic-fireworks");
        GameSpineUtil.addToSlot(this.animation, this.counter, "dynamic-value");
        GameSpineUtil.addToSlot(this.animation, this.winTitle, "dynamic-label-0");
        GameSpineUtil.addToSlot(this.animation, this.bigWinTitle, "dynamic-label-1");
        GameSpineUtil.addToSlot(this.animation, this.epicWinTitle, "dynamic-label-2");
        GameSpineUtil.addToSlot(this.animation, this.megaWinTitle, "dynamic-label-3");
        GameSpineUtil.addToSlot(this.lowWinAnimation, this.lowWinTitle, "dynamic-slot-label-0");
        GameSpineUtil.addToSlot(this.lowWinAnimation, this.lowWinCounter, "dynamic-slot-label-0");
    }
    async handlePlayScatterWin() {
        this.visible = true;
        if (this.isLowWin()) {
            await this.playLowWin();
        }
        else {
            await Promise.all([this.playAppear(), this.playCount()]);
            await this.playDisappear();
        }
        this.handleAnimationComplete();
    }
    async playLowWin() {
        const totalWin = this.model.getChanceGame().getTotalWin();
        this.lowWinCounter.setText(this.formatCurrency(totalWin));
        this.showOverlay();
        await new Promise(resolve => {
            SpineUtil.play(this.lowWinAnimation, "panel-0", resolve);
            GameSpineUtil.forceUpdate(this.lowWinAnimation);
        });
        this.hideOverlay();
    }
    async playAppear() {
        this.dispatchGame(SlingoEvent.SCATTER_WIN_APPEAR);
        this.showOverlay();
        SpineUtil.playLoop(this.animation, "loop");
        await gsap.fromTo(this.animation, { alpha: 0 }, { alpha: 1, duration: TweenConstants.DEFAULT_OVERLAY_FADE_DURATION });
    }
    async playDisappear() {
        this.dispatchGame(SlingoEvent.SCATTER_WIN_DISAPPEAR);
        this.fireworkBurst.stop();
        this.hideOverlay();
        const multiplier = this.model.getChanceGame().getParams().getMultiplier();
        const bigWinLevel = this.model.getBigWinLevel(multiplier);
        if (bigWinLevel >= 0) {
            this.hideBigWin(bigWinLevel);
        }
        else {
            this.hideWin();
        }
        await gsap.to(this.animation, { alpha: 0, duration: TweenConstants.DEFAULT_OVERLAY_FADE_DURATION });
    }
    async playCount() {
        this.dispatchGame(SlingoEvent.SCATTER_WIN_COUNT);
        this.timelineAnimation?.kill();
        const timeline = gsap.timeline();
        this.timelineAnimation = timeline;
        const totalWin = this.model.getChanceGame().getTotalWin();
        const multiplier = this.model.getChanceGame().getParams().getMultiplier();
        const totalDuration = this.getCounterDuration();
        const thresholdValues = this.getThresholdValues(totalWin, this.model.getTotalStaked());
        const counterDuration = totalDuration / (thresholdValues.length + 1);
        this.initialiseCounter();
        this.showWin();
        // Count up to each threshold
        thresholdValues.forEach((threshold, index) => {
            timeline
                .to(this, {
                counterValue: threshold,
                duration: counterDuration,
                onUpdate: () => this.updateCounter(),
                ease: "power1.in",
            })
                .call(() => this.showBigWin(index));
        });
        // Prevent rollup to total win from being slower than the last threshold
        const lastThresholdValue = thresholdValues[thresholdValues.length - 1];
        const secondLastThresholdValue = thresholdValues[thresholdValues.length - 2] || 0;
        const lastThresholdRateOfIncrease = (lastThresholdValue - secondLastThresholdValue) / counterDuration;
        const totalWinRateOfIncrease = (totalWin - lastThresholdValue) / counterDuration;
        const remainingDuration = lastThresholdRateOfIncrease > 0 ? totalWinRateOfIncrease / lastThresholdRateOfIncrease : counterDuration;
        // console.log("BIG WIN", thresholdValues, counterDuration, remainingDuration)
        // Count up to the total win amount
        timeline.to(this, {
            counterValue: totalWin,
            duration: Math.min(counterDuration, remainingDuration),
            onUpdate: () => this.updateCounter(),
            ease: "power1.in",
        });
        if (thresholdValues.length > 0) {
            this.fireworkBurst.start();
        }
        this.mouseBlocker.interactiveChildren = true;
        await this.timelineAnimation;
        this.mouseBlocker.interactiveChildren = false;
        this.dispatchGame(SlingoEvent.SCATTER_WIN_COUNT_END);
        SpineUtil.play(this.animation, "celebrate", null, null, 2);
        this.timelineAnimation = gsap.to(this, { duration: 3 });
        await this.timelineAnimation;
    }
    initialiseCounter() {
        this.counter.setText(this.formatCurrency(0));
    }
    updateCounter() {
        this.counter.setText(this.formatCurrency(this.counterValue));
    }
    showWin() {
        SpineUtil.play(this.animation, "0-win", null, null, 1);
    }
    hideWin() {
        SpineUtil.play(this.animation, "0-win-out", null, null, 1);
    }
    showBigWin(level) {
        SpineUtil.play(this.animation, `${level + 1}-win`, null, null, 1);
    }
    hideBigWin(level) {
        SpineUtil.play(this.animation, `${level + 1}-win-out`, null, null, 1);
    }
    getCounterDuration() {
        const multiplier = this.model.getChanceGame().getParams().getMultiplier();
        const bigWinLevel = this.model.getBigWinLevel(multiplier);
        return this.uiProperties.counterTimes[bigWinLevel + 1];
    }
    isLowWin() {
        return this.model.getChanceGame().getParams().getMultiplier() <= 1;
    }
    handleResize() {
        // Do nothing, as the base class does unwanted responsive scaling.
    }
    getThresholdValues(win, totalStake) {
        return this.model.getBigWinThresholds()
            .map(threshold => threshold * totalStake)
            .filter(threshold => win >= threshold);
    }
}
//# sourceMappingURL=GameScatterWin.js.map